import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { VehicleInformationModel } from '../models'
import { fetchVehicleInformation } from '../api'

import { TimeInMilliseconds } from 'src/Libs/types'
import { usePortalQuery } from 'src/Libs/api'

interface UseVehicleInformation {
  vehicleInformation: VehicleInformationModel | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
  hasOutdatedData: boolean
}

const FETCH_INTERVAL = TimeInMilliseconds.thirtySecond
const STALE_TIME = TimeInMilliseconds.oneMinute
const MAX_RETRY_ATTEMPTS = 3
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond

/**
 * Hook for handling fetching of vehicle information of user
 */
export const useVehicleInformation = (installationId: number, vehicleId: VehicleInformationModel['id']): UseVehicleInformation => {
  const queryKey = ['vehicle-integration', 'vehicle-information', vehicleId]
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<VehicleInformationModel | null> => {
    const { error: fetchError, hasErrors, value } = await fetchVehicleInformation(installationId, vehicleId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, isError, isStale } = usePortalQuery({
    queryKey,
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    /** retries after 0.5, 1, 2 seconds */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)
    },
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: FETCH_INTERVAL,
  })

  return { vehicleInformation: data ?? null, loading: isLoading, hasError: isError, hasOutdatedData: isStale }
}
